import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Plus, Trash2, Loader2, RefreshCw, AlertTriangle, Play, Radar, Video, Users } from 'lucide-react'
import { Card, Button } from '../ui'
import { Organization } from '../../services/organizationService'
import { scoutAdminService, ScoutBrandHint, ScoutScanError } from '../../services/scoutAdminService'
import { ScoutAccount } from '../../types/scout'

interface OrganizationBrandScoutProps {
  organization: Organization
}

const TERMINAL = ['completed', 'failed', 'partial']

const PLATFORM_NAMES: Record<number, string> = { 1: 'Instagram', 2: 'TikTok', 11: 'Facebook' }
const platformName = (id: number): string => PLATFORM_NAMES[id] || `Platform ${id}`

const OrganizationBrandScout: React.FC<OrganizationBrandScoutProps> = ({ organization }) => {
  const customerId = organization.customerId

  // ── Brand hints state ──
  const [hints, setHints] = useState<ScoutBrandHint[]>([])
  const [accounts, setAccounts] = useState<ScoutAccount[]>([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)

  // Add-hint form
  const [newBrand, setNewBrand] = useState('')
  const [newAliases, setNewAliases] = useState('')
  const [newScope, setNewScope] = useState<string>('') // '' = all accounts
  const [saving, setSaving] = useState(false)

  // ── Scout account management state ──
  const [savingAcctId, setSavingAcctId] = useState<string | null>(null)
  const [rescanningId, setRescanningId] = useState<string | null>(null)

  // ── Reanalyze state ──
  const [reAccountId, setReAccountId] = useState('')
  const [reDays, setReDays] = useState(30)
  const [postId, setPostId] = useState('')
  const [triggering, setTriggering] = useState(false)
  const [activeScanId, setActiveScanId] = useState<string | null>(null)
  const [scanStatus, setScanStatus] = useState<string | null>(null)
  const [scanErrors, setScanErrors] = useState<ScoutScanError[]>([])
  const [watching, setWatching] = useState(false) // actively polling this scan?
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    setMessage(null)
    // Load hints and accounts independently — a failure in one must not blank the
    // other (e.g. the Reanalyze account picker should populate even if the brand-hint
    // query fails).
    const [hintsRes, acctRes] = await Promise.allSettled([
      scoutAdminService.listBrandHints(customerId),
      scoutAdminService.listAccounts(customerId)
    ])

    if (hintsRes.status === 'fulfilled') {
      setHints(hintsRes.value.data || [])
    }
    if (acctRes.status === 'fulfilled') {
      setAccounts(acctRes.value.data || [])
    }

    const failure = [hintsRes, acctRes].find(
      (r): r is PromiseRejectedResult => r.status === 'rejected'
    )
    if (failure) {
      setMessage({
        kind: 'error',
        text: failure.reason instanceof Error ? failure.reason.message : 'Failed to load some data'
      })
    }
    setLoading(false)
  }, [customerId])

  useEffect(() => {
    loadData()
  }, [loadData])

  // ── Brand hint actions ──
  const handleAddHint = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newBrand.trim()) {
      setMessage({ kind: 'error', text: 'Brand name is required' })
      return
    }
    setSaving(true)
    setMessage(null)
    try {
      const aliases = newAliases.split(',').map((a) => a.trim()).filter(Boolean)
      await scoutAdminService.createBrandHint(customerId, {
        brandName: newBrand.trim(),
        brandAliases: aliases,
        scoutAccountId: newScope || null
      })
      setNewBrand('')
      setNewAliases('')
      setNewScope('')
      setMessage({ kind: 'success', text: 'Brand hint added' })
      await loadData()
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Failed to add brand hint' })
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (hint: ScoutBrandHint) => {
    try {
      await scoutAdminService.updateBrandHint(hint.id, customerId, { isActive: !hint.is_active })
      await loadData()
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Failed to update' })
    }
  }

  const handleDeleteHint = async (hint: ScoutBrandHint) => {
    try {
      await scoutAdminService.deleteBrandHint(hint.id, customerId)
      setHints((prev) => prev.filter((h) => h.id !== hint.id))
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Failed to delete' })
    }
  }

  // ── Scout account management actions ──

  const handleToggleAcctFlag = async (
    account: ScoutAccount,
    field: 'is_owned' | 'video_analysis_enabled'
  ) => {
    setSavingAcctId(account.id)
    setMessage(null)
    try {
      await scoutAdminService.updateAccount(account.id, customerId, { [field]: !account[field] })
      // Optimistic local update (avoids a full reload flicker)
      setAccounts((prev) => prev.map((a) => (a.id === account.id ? { ...a, [field]: !account[field] } : a)))
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Failed to update account' })
    } finally {
      setSavingAcctId(null)
    }
  }

  // ── Reanalyze actions ──
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    setWatching(false)
  }, [])

  // Clean up any in-flight poll when the drawer/component unmounts.
  useEffect(() => stopPolling, [stopPolling])

  // Fetch terminal errors for a failed/partial scan (best-effort).
  const loadScanErrors = useCallback(async (scanId: string) => {
    try {
      const errRes = await scoutAdminService.getScanErrors(scanId, customerId)
      setScanErrors(errRes.data || [])
    } catch { /* non-fatal — status already shown */ }
  }, [customerId])

  const pollScan = useCallback((scanId: string) => {
    setActiveScanId(scanId)
    setScanStatus('pending')
    setScanErrors([])
    stopPolling()
    setWatching(true)

    // A reanalyze can run many minutes; polling must survive transient blips (5xx, session
    // refresh, network hiccups) instead of dying on the first error and stranding the spinner.
    let consecutiveErrors = 0
    const startedAt = Date.now()
    const MAX_MS = 30 * 60 * 1000        // safety cap: stop watching after 30 min
    const MAX_CONSECUTIVE_ERRORS = 4     // ~20s of failures before giving up

    pollRef.current = setInterval(async () => {
      if (Date.now() - startedAt > MAX_MS) {
        stopPolling()
        setMessage({ kind: 'error', text: 'Stopped watching after 30 min. The scan may still be running — use "Check status" to refresh.' })
        return
      }
      try {
        const res = await scoutAdminService.getScanStatus(scanId, customerId)
        consecutiveErrors = 0
        const status = res.data?.status
        setScanStatus(status)
        if (status && TERMINAL.includes(status)) {
          stopPolling()
          if (status === 'failed' || status === 'partial') await loadScanErrors(scanId)
        }
      } catch {
        consecutiveErrors++
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          stopPolling()
          setMessage({ kind: 'error', text: 'Lost contact with the scan-status endpoint. The scan may still be running — use "Check status" to retry.' })
        }
      }
    }, 5000)
  }, [customerId, stopPolling, loadScanErrors])

  // Manual one-off status check that re-attaches polling if the scan is still running.
  const checkStatusNow = useCallback(async () => {
    if (!activeScanId) return
    try {
      const res = await scoutAdminService.getScanStatus(activeScanId, customerId)
      const status = res.data?.status
      setScanStatus(status)
      if (status && TERMINAL.includes(status)) {
        if (status === 'failed' || status === 'partial') await loadScanErrors(activeScanId)
      } else {
        pollScan(activeScanId) // still running — resume watching
      }
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Could not fetch scan status' })
    }
  }, [activeScanId, customerId, loadScanErrors, pollScan])

  const handleReanalyzeAccount = async () => {
    if (!reAccountId) {
      setMessage({ kind: 'error', text: 'Pick an account to reanalyze' })
      return
    }
    setTriggering(true)
    setMessage(null)
    try {
      const res = await scoutAdminService.reanalyzeAccount(reAccountId, customerId, reDays)
      setMessage({ kind: 'success', text: 'Reanalyze started' })
      if (res.data?.scanId) pollScan(res.data.scanId)
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Failed to start reanalyze' })
    } finally {
      setTriggering(false)
    }
  }

  const handleRescan = async (account: ScoutAccount) => {
    setRescanningId(account.id)
    setMessage(null)
    try {
      const res = await scoutAdminService.rescanAccount(account.id, customerId, 30)
      setMessage({ kind: 'success', text: `Rescan started for @${account.username}` })
      if (res.data?.scanId) pollScan(res.data.scanId)
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Failed to start rescan' })
    } finally {
      setRescanningId(null)
    }
  }

  const handleReplayPost = async () => {
    if (!postId.trim()) {
      setMessage({ kind: 'error', text: 'Enter a post ID' })
      return
    }
    setTriggering(true)
    setMessage(null)
    try {
      const res = await scoutAdminService.reanalyzePost(postId.trim(), customerId)
      setMessage({ kind: 'success', text: 'Replay started' })
      setPostId('')
      if (res.data?.scanId) pollScan(res.data.scanId)
    } catch (err) {
      // The API returns a clear message when the post is older than 31 days / not stored.
      setMessage({ kind: 'error', text: err instanceof Error ? err.message : 'Failed to start replay' })
    } finally {
      setTriggering(false)
    }
  }

  const scopeLabel = (hint: ScoutBrandHint) =>
    hint.scout_account_id ? `@${hint.scout_account_username ?? 'account'}` : 'All accounts'

  return (
    <div className='space-y-6'>
      {message && (
        <div
          className={`px-4 py-3 rounded-lg border text-sm ${
            message.kind === 'success'
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* ── Scout accounts (support-managed flags) ── */}
      <Card variant='outlined' className='p-4'>
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center space-x-2'>
            <Users className='h-4 w-4 text-avi-primary' />
            <h3 className='text-sm font-medium text-gray-900'>Scout Accounts</h3>
          </div>
          <button
            onClick={loadData}
            className='p-1.5 hover:bg-gray-100 rounded-lg transition-colors'
            title='Refresh'
          >
            <RefreshCw className={`h-4 w-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <p className='text-xs text-gray-500 mb-4'>
          <span className='font-medium'>Owned</span> marks the customer’s own accounts.{' '}
          <span className='font-medium'>Video analysis</span> downloads &amp; analyzes video (via paid
          residential proxy) — enable only where the extra cost is worthwhile. These flags are
          support-managed and not customer-editable.
        </p>

        {/* Accounts table */}
        <div className='overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b border-gray-200 text-left text-xs text-gray-500'>
                <th className='py-2'>Account</th>
                <th className='py-2'>Owned</th>
                <th className='py-2'>Video Analysis</th>
                <th className='py-2'>Active</th>
                <th className='py-2 text-right'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {accounts.length === 0 && (
                <tr>
                  <td colSpan={5} className='py-4 text-center text-gray-400'>
                    {loading ? 'Loading…' : 'No scout accounts yet'}
                  </td>
                </tr>
              )}
              {accounts.map((a) => (
                <tr key={a.id} className='border-b border-gray-100'>
                  <td className='py-2 font-medium text-gray-900'>
                    <span className='inline-block mr-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 align-middle'>{platformName(a.platform_id)}</span>
                    {a.platform_id === 11 ? a.username : `@${a.username}`}
                  </td>
                  <td className='py-2'>
                    <button
                      onClick={() => handleToggleAcctFlag(a, 'is_owned')}
                      disabled={savingAcctId === a.id}
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        a.is_owned ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {a.is_owned ? 'Owned' : 'Not owned'}
                    </button>
                  </td>
                  <td className='py-2'>
                    <button
                      onClick={() => handleToggleAcctFlag(a, 'video_analysis_enabled')}
                      disabled={savingAcctId === a.id}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        a.video_analysis_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      <Video className='h-3 w-3' />
                      {a.video_analysis_enabled ? 'On' : 'Off'}
                      {savingAcctId === a.id && <Loader2 className='h-3 w-3 animate-spin' />}
                    </button>
                  </td>
                  <td className='py-2'>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      a.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {a.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className='py-2 text-right'>
                    <button
                      onClick={() => handleRescan(a)}
                      disabled={rescanningId === a.id}
                      title='Full fresh scrape + analyze (downloads new media incl. video)'
                      className='inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors'
                    >
                      {rescanningId === a.id ? <Loader2 className='h-3 w-3 animate-spin' /> : <RefreshCw className='h-3 w-3' />}
                      Rescan
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Brand-hint watchlist ── */}
      <Card variant='outlined' className='p-4'>
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center space-x-2'>
            <Radar className='h-4 w-4 text-avi-primary' />
            <h3 className='text-sm font-medium text-gray-900'>Brand Hint Watchlist</h3>
          </div>
          <button
            onClick={loadData}
            className='p-1.5 hover:bg-gray-100 rounded-lg transition-colors'
            title='Refresh'
          >
            <RefreshCw className={`h-4 w-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <p className='text-xs text-gray-500 mb-4'>
          Brands listed here are passed to the AI during analysis to improve detection. Leave the scope as
          “All accounts” to apply customer-wide, or pin a hint to a single account.
        </p>

        {/* Add form */}
        <form onSubmit={handleAddHint} className='grid grid-cols-12 gap-2 mb-4'>
          <input
            type='text'
            value={newBrand}
            onChange={(e) => setNewBrand(e.target.value)}
            placeholder='Brand name (e.g. Vita Coco)'
            className='col-span-4 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-avi-primary'
          />
          <input
            type='text'
            value={newAliases}
            onChange={(e) => setNewAliases(e.target.value)}
            placeholder='Aliases, comma-separated'
            className='col-span-4 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-avi-primary'
          />
          <select
            value={newScope}
            onChange={(e) => setNewScope(e.target.value)}
            className='col-span-3 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-avi-primary'
          >
            <option value=''>All accounts</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>@{a.username}</option>
            ))}
          </select>
          <Button type='submit' disabled={saving} className='col-span-1'>
            {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : <Plus className='h-4 w-4' />}
          </Button>
        </form>

        {/* Table */}
        <div className='overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b border-gray-200 text-left text-xs text-gray-500'>
                <th className='py-2'>Brand</th>
                <th className='py-2'>Aliases</th>
                <th className='py-2'>Scope</th>
                <th className='py-2'>Active</th>
                <th className='py-2'></th>
              </tr>
            </thead>
            <tbody>
              {hints.length === 0 && (
                <tr>
                  <td colSpan={5} className='py-4 text-center text-gray-400'>
                    {loading ? 'Loading…' : 'No brand hints yet'}
                  </td>
                </tr>
              )}
              {hints.map((hint) => (
                <tr key={hint.id} className='border-b border-gray-100'>
                  <td className='py-2 font-medium text-gray-900'>{hint.brand_name}</td>
                  <td className='py-2 text-gray-600'>{(hint.brand_aliases || []).join(', ') || '—'}</td>
                  <td className='py-2 text-gray-600'>{scopeLabel(hint)}</td>
                  <td className='py-2'>
                    <button
                      onClick={() => handleToggleActive(hint)}
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        hint.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {hint.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className='py-2 text-right'>
                    <button
                      onClick={() => handleDeleteHint(hint)}
                      className='p-1.5 hover:bg-red-50 rounded-lg transition-colors'
                      title='Delete'
                    >
                      <Trash2 className='h-4 w-4 text-red-500' />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Reanalyze ── */}
      <Card variant='outlined' className='p-4'>
        <h3 className='text-sm font-medium text-gray-900 mb-4'>Reanalyze</h3>

        {/* Account reanalyze */}
        <div className='mb-6'>
          <label className='block text-xs font-medium text-gray-700 mb-2'>Reanalyze an account</label>
          <div className='flex items-end gap-2'>
            <select
              value={reAccountId}
              onChange={(e) => setReAccountId(e.target.value)}
              className='flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-avi-primary'
            >
              <option value=''>Select account…</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>@{a.username}</option>
              ))}
            </select>
            <div>
              <label className='block text-[11px] text-gray-500'>Lookback (days, max 180)</label>
              <input
                type='number'
                min={1}
                max={180}
                value={reDays}
                onChange={(e) => setReDays(Math.min(180, Math.max(1, parseInt(e.target.value) || 30)))}
                className='w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-avi-primary'
              />
            </div>
            <Button onClick={handleReanalyzeAccount} disabled={triggering}>
              {triggering ? <Loader2 className='h-4 w-4 animate-spin' /> : <RefreshCw className='h-4 w-4' />}
              <span className='ml-1.5'>Reanalyze</span>
            </Button>
          </div>
        </div>

        {/* Single-post replay */}
        <div>
          <label className='block text-xs font-medium text-gray-700 mb-2'>Replay a single post</label>
          <div className='flex items-end gap-2'>
            <input
              type='text'
              value={postId}
              onChange={(e) => setPostId(e.target.value)}
              placeholder='Post ID (must be < 31 days old)'
              className='flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-avi-primary'
            />
            <Button onClick={handleReplayPost} disabled={triggering} variant='outline'>
              {triggering ? <Loader2 className='h-4 w-4 animate-spin' /> : <Play className='h-4 w-4' />}
              <span className='ml-1.5'>Replay</span>
            </Button>
          </div>
        </div>

        {/* Scan status + errors */}
        {activeScanId && (
          <div className='mt-4 border-t border-gray-100 pt-4'>
            <div className='flex items-center gap-2 text-sm'>
              <span className='text-gray-500'>Scan status:</span>
              <span className='font-medium text-gray-900'>{scanStatus || '—'}</span>
              {watching && <Loader2 className='h-3.5 w-3.5 animate-spin text-gray-400' />}
              {!watching && scanStatus && !TERMINAL.includes(scanStatus) && (
                <button
                  onClick={checkStatusNow}
                  className='inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800'
                >
                  <RefreshCw className='h-3 w-3' /> Check status
                </button>
              )}
            </div>
            {scanErrors.length > 0 && (
              <div className='mt-3 space-y-1'>
                <div className='flex items-center gap-1.5 text-xs font-medium text-red-600'>
                  <AlertTriangle className='h-3.5 w-3.5' />
                  {scanErrors.length} error{scanErrors.length > 1 ? 's' : ''}
                </div>
                {scanErrors.map((err) => (
                  <div key={err.id} className='text-xs text-gray-600 bg-red-50 rounded px-2 py-1'>
                    <span className='font-medium'>[{err.stage}{err.error_code ? `/${err.error_code}` : ''}]</span>{' '}
                    {err.platform_post_id ? `post ${err.platform_post_id}: ` : ''}{err.error_text}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  )
}

export default OrganizationBrandScout
